/**
 * Local game driver for Solo and Tutorial.
 *
 * All rules come from @kikik/mahjong-core, all bot moves from
 * @kikik/mahjong-bot and all explanations from @kikik/mahjong-coach. This hook
 * only decides *when* to submit an action and turns events into log lines.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { applyAction, isGameOver } from "@kikik/mahjong-core";
import type { Action, GameEvent, GameState, PlayerId } from "@kikik/mahjong-core";
import { decideBotAction } from "@kikik/mahjong-bot";
import { getAdvice } from "@kikik/mahjong-coach";
import type { CoachAdvice } from "@kikik/mahjong-coach";
import { msg } from "@kikik/i18n";
import type { Message } from "@kikik/i18n";
import { describeEvent } from "./eventLog";
import type { LogEntry } from "./eventLog";
import { fromGameState } from "./tableModel";
import type { TableModel } from "./tableModel";

export type SeatController = "human" | "bot";

export const DEFAULT_CONTROLLERS: readonly SeatController[] = ["human", "bot", "bot", "bot"];
export const DEFAULT_BOT_DELAY_MS = 550;

export interface LocalGameOptions {
  createState: () => GameState;
  controllers?: readonly SeatController[];
  botDelayMs?: number;
  you?: PlayerId;
  /** Coach panel on by default in Tutorial, off in Solo (hint on demand). */
  autoAdvice?: boolean;
  /** Normal play draws for the local human; lessons deliberately leave this off. */
  autoDraw?: boolean;
}

export interface LocalGame {
  state: GameState;
  model: TableModel;
  you: PlayerId;
  log: LogEntry[];
  /** Why the last action was refused, as a message the UI renders. */
  notice: Message | null;
  advice: CoachAdvice | null;
  hintTileId: number | null;
  submit: (action: Action) => void;
  showHint: () => void;
  clearNotice: () => void;
  reset: (state?: GameState) => void;
}

/** The seat a bot should act for, including inside a claim window. */
function botSeat(state: GameState, controllers: readonly SeatController[]): PlayerId | null {
  if (isGameOver(state)) return null;
  if (state.phase === "awaiting-claims") {
    return state.pendingClaims?.waitingOn.find((id) => controllers[id] === "bot") ?? null;
  }
  return controllers[state.turn] === "bot" ? state.turn : null;
}

export function useLocalGame(options: LocalGameOptions): LocalGame {
  const controllers = options.controllers ?? DEFAULT_CONTROLLERS;
  const controllerKey = controllers.join(",");
  const botDelayMs = options.botDelayMs ?? DEFAULT_BOT_DELAY_MS;
  const you = options.you ?? 0;

  const [state, setState] = useState<GameState>(options.createState);
  const [log, setLog] = useState<LogEntry[]>([]);
  const [notice, setNotice] = useState<Message | null>(null);
  const [hintTileId, setHintTileId] = useState<number | null>(null);
  const [adviceShown, setAdviceShown] = useState(options.autoAdvice ?? false);

  const logId = useRef(0);
  const automaticDraws = useRef(new Set<number>());
  const stateRef = useRef(state);
  stateRef.current = state;
  const controllersRef = useRef(controllers);
  controllersRef.current = controllers;

  const appendEvents = useCallback(
    (events: GameEvent[]) => {
      const entries = events
        .map((event) => describeEvent(event, you))
        .filter((entry): entry is Omit<LogEntry, "id"> => entry !== null)
        .map((entry) => {
          logId.current += 1;
          return { ...entry, id: logId.current };
        });
      if (entries.length === 0) return;
      setLog((previous) => [...previous, ...entries].slice(-80));
    },
    [you],
  );

  const submit = useCallback(
    (action: Action) => {
      const result = applyAction(stateRef.current, action);
      if (!result.ok) {
        setNotice(msg(`error.${result.error.code}`));
        return;
      }
      stateRef.current = result.state;
      setState(result.state);
      setNotice(null);
      setHintTileId(null);
      appendEvents(result.events);
    },
    [appendEvents],
  );

  // A draw is the only decision-free human action. The action-count guard is
  // important under React Strict Mode: an effect may be replayed, but a legal
  // draw opportunity must be submitted exactly once.
  useEffect(() => {
    if (!options.autoDraw || controllersRef.current[you] !== "human") return;
    if (state.phase !== "awaiting-draw" || state.turn !== you) return;
    const draw = getLegalDraw(state, you);
    if (!draw || automaticDraws.current.has(state.actionCount)) return;
    automaticDraws.current.add(state.actionCount);
    submit(draw);
  }, [options.autoDraw, state, submit, you]);

  // Bots move on a timer so a beginner can follow the table.
  useEffect(() => {
    const seat = botSeat(state, controllersRef.current);
    if (seat === null) return;
    const timer = setTimeout(() => {
      const action = decideBotAction(state, seat);
      if (action) submit(action);
    }, botDelayMs);
    return () => clearTimeout(timer);
  }, [state, controllerKey, botDelayMs, submit]);

  const model = useMemo(() => fromGameState(state, you, () => null), [state, you]);

  const advice = useMemo(() => {
    if (!adviceShown) return null;
    return getAdvice(state, you);
  }, [adviceShown, state, you]);

  const showHint = useCallback(() => {
    const next = getAdvice(stateRef.current, you);
    setAdviceShown(true);
    setHintTileId(next.tileId);
    setNotice(null);
  }, [you]);

  const reset = useCallback(
    (next?: GameState) => {
      const fresh = next ?? options.createState();
      stateRef.current = fresh;
      setState(fresh);
      setLog([]);
      setNotice(null);
      setHintTileId(null);
      setAdviceShown(options.autoAdvice ?? false);
      automaticDraws.current.clear();
      logId.current = 0;
    },
    [options],
  );

  return {
    state,
    model,
    you,
    log,
    notice,
    advice,
    hintTileId,
    submit,
    showHint,
    clearNotice: useCallback(() => setNotice(null), []),
    reset,
  };
}

function getLegalDraw(state: GameState, playerId: PlayerId): Action | null {
  if (state.phase !== "awaiting-draw" || state.turn !== playerId || state.wall.length === 0) {
    return null;
  }
  return { type: "draw", playerId };
}
